import '@mantine/carousel/styles.css';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { Button } from '@mantine/core';
import { useCountdown } from './common/useCountdown';
import { useLogout } from 'src/auth/hooks/useLogout';
import { RedirectToLoginPage } from './auth/RedirectToLoginPage';
import ClientsPage from './clients/ClientsPage';
import RedirectUnknownClient from './clients/RedirectUnknownClient';
import { useIsAfternoon } from './common/useIsAfternoonHook';
import { useFlags } from './feature-flags/FlagsHook';
import { useCurrentClientIfAvailable } from './clients/context/CurrentClientProvider';
import DetectedEntitiesPage from './data-capture/detected-entities/DetectedEntitiesPage';
import { DebugPage } from './debug/DebugPage';
import ViewEntityPage from './financial-data/ViewEntityPage';
import ClientData, { ClientTabs } from './home/ClientData';
import { CompleteConnectionFlowPage } from 'src/financial/connections/CompleteConnectionFlowPage';
import SecretMenuPage from './secret-menu/SecretMenuPage';
import { AppContainer, RoutePane } from './UI/app-container';
import DeprecatedAppContainer from './UI/app-container/DeprecatedAppContainer';
import { useClients } from 'src/clients/hooks/useClients';
import PageLoader from 'src/UI/loader/PageLoader';
import CreateFinancialItemPage from 'src/financial/financial-data/financial-item/CreateFinancialItemPage';
import ViewAndEditFinancialItemPage from 'src/financial/financial-data/financial-item/ViewAndEditFinancialItemPage';
import AdminPage from './admin/AdminPage';
import { SessionData, useSessionData } from './auth/SessionDataHook';
import useCurrentUser from './admin/users/hooks/useCurrentUser';
import UserDetailsPage from './user/UserDetailsPage';
import { GenericErrorPage } from './error/GenericErrorPage';
import CompanyPage from './company/CompanyPage';
import FundDetailPage from 'src/company/funds/components/FundDetailPage';
import { OAuthRedirectPage } from 'src/meetings/oauth-redirect/OAuthRedirectPage';
import { MeetingsPage } from 'src/meetings/home/MeetingsPage';
import { MeetingDetailPage } from 'src/meetings/detail/MeetingDetailPage';
import SuitabilityReportEditPage from 'src/suitability-report/edit/SuitabilityReportEditPage';
import MeetingAgendaEditPage from 'src/meeting-agenda/edit/MeetingAgendaEditPage';
import DataExportEditPage from './data-export/edit/DataExportEditPage';
import NewSuitabilityFlow from './suitability-report/NewSuitabilityFlow';
import AgendaFlowPage from './meeting-agenda/AgendaFlowPage';
import MeetingNoteEditPage from './meeting-note/edit/MeetingNoteEditPage';
import MeetingNoteFlowPage from './meeting-note/MeetingNoteFlowPage';
import DataExportFlowPage from './data-export/DataExportFlowPage';
import { InvitePageWithFlag } from 'src/auth/invite/InvitePageWithFlag';
import { AcceptLinkPage } from 'src/auth/accept-link/AcceptLinkPage';
import { LoginPageWithFlag } from 'src/auth/login/LoginPageWithFlag';
import { DocsOAuthRedirectPage } from 'src/secret-menu/sharepoint/DocsOAuthRedirectPage';
import { LogoutPage } from 'src/auth/logout/LogoutPage';
import { RecoveryPageWithFlag } from 'src/auth/recovery/RecoveryPageWithFlag';
import { SettingsPageWithFlag } from 'src/auth/settings/SettingsPageWithFlag';
import { SsoMismatchPage } from 'src/auth/sso-mismatch/SsoMismatchPage';
import { SuccessMessage } from 'src/auth/components/SuccessMessage';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';
import { AccountDeletionPage } from 'src/auth/deletion/AccountDeletionPage';
import { Suspense } from 'react';

function isRecoverySession(session: SessionData): boolean {
  const authMethods = session.authentication_methods ?? [];
  return authMethods.some(m => m.method === 'code_recovery');
}

export default function AllRoutesNew() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPageWithFlag />} />
        <Route path="/accept-link" element={<AcceptLinkPage />} />
        <Route path="/invite" element={<InvitePageWithFlag />} />
        <Route path="/recovery" element={<RecoveryPageWithFlag />} />
        <Route path="/settings" element={<SettingsPageWithFlag />} />
        <Route path="/sso-mismatch" element={<SsoMismatchPage />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route path="/account-deletion" element={<AccountDeletionPage />} />
        <Route path="*" element={<ProtectedRoutesWithSessionNew />} />
      </Routes>
    </Suspense>
  );
}

function ProtectedRoutesWithSessionNew() {
  const { isLoading, session } = useSessionData();

  if (isLoading) return <PageLoader />;
  if (!session) return <RedirectToLoginPage />;

  const hasRecoverySession = isRecoverySession(session);
  if (hasRecoverySession) {
    return <RecoverySessionInterstitial />;
  }

  return <ProtectedRoutesWithUserCheck />;
}

function RecoverySessionInterstitial() {
  const handleGoToLogin = () => {
    window.location.href = '/logout';
  };

  return (
    <AuthPageLayout>
      <SuccessMessage variant="sso_linked" onAction={handleGoToLogin} />
    </AuthPageLayout>
  );
}

function AdminRoute() {
  const { isSystemAdmin, isLoading } = useCurrentUser();
  const location = useLocation();

  if (isLoading) return <PageLoader />;

  if (!isSystemAdmin) {
    const fallback = location.state?.from?.pathname || '/';
    return <Navigate to={fallback} replace />;
  }

  return <Outlet />;
}

function ProtectedRoutesWithUserCheck() {
  const { currentUser, isLoading: isLoadingUser } = useCurrentUser();
  const { mutate: logout } = useLogout({
    onError: () => {
      window.location.replace('/login');
    },
  });

  const isUserInactive =
    !isLoadingUser && currentUser && !['ACTIVE', 'PENDING'].includes(currentUser.userStatus);
  const countdown = useCountdown(30, !!isUserInactive, () => logout());

  if (isLoadingUser) return <PageLoader />;

  if (!currentUser) {
    return <GenericErrorPage error="Current user is not available. Please try logging in again." />;
  }

  if (!['ACTIVE', 'PENDING'].includes(currentUser.userStatus)) {
    return (
      <GenericErrorPage
        error="Your account is not active. Please contact your system administrator."
        title="Account inactive"
        text={
          <>
            Your account is not active. Please contact your system administrator. You will be logged
            out in{' '}
            <span
              style={{
                display: 'inline-block',
                minWidth: '2ch',
                textAlign: 'center',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {countdown}
            </span>{' '}
            seconds.
          </>
        }
        showRefresh={false}
        actions={
          <Button variant="filled" size="md" onClick={() => logout()}>
            Log out now
          </Button>
        }
      />
    );
  }

  return <ProtectedRoutes />;
}

function ProtectedRoutes() {
  const { flagEnabledNewLayout } = useFlags();

  if (flagEnabledNewLayout) return <NewProtectedRoutes />;
  return <DeprecatedProtectedRoutes />;
}

function NewProtectedRoutes() {
  const { isAfternoon } = useIsAfternoon();
  const {
    flagEnabledDocumentEdit,
    flagEnabledMeetingNoteEdit,
    flagEnabledNewSuitabilityReportAccess,
  } = useFlags();

  return (
    <Routes>
      <Route path="/" element={<AppContainer />}>
        <Route index element={<Navigate to="/clients/all" replace />} />
        <Route path="clients/:tab/:id?" element={<ClientsPage />} />
        <Route path="client/undefined" element={<RedirectUnknownClient />} />

        {flagEnabledDocumentEdit && (
          <Route element={<RoutePane bg="white" />}>
            <Route
              path="client/:clientId/document/suitability-reports/:id/edit"
              element={<SuitabilityReportEditPage />}
            />
            <Route
              path="client/:clientId/document/data-exports/:id/edit"
              element={<DataExportEditPage />}
            />
            <Route
              path="client/:clientId/document/meeting-agendas/:id/edit"
              element={<MeetingAgendaEditPage />}
            />
          </Route>
        )}

        {flagEnabledMeetingNoteEdit && (
          <Route element={<RoutePane bg="white" />}>
            <Route
              path="client/:clientId/document/meeting-notes/:id/edit"
              element={<MeetingNoteEditPage />}
            />
          </Route>
        )}

        {flagEnabledNewSuitabilityReportAccess && (
          <Route element={<RoutePane bg="white" skipDefaultSidebar />}>
            <Route path="client/:clientId/suitability-report" element={<NewSuitabilityFlow />} />
          </Route>
        )}

        <Route element={<RoutePane bg="white" skipDefaultSidebar />}>
          <Route path="client/:clientId/meeting-agenda" element={<AgendaFlowPage />} />
          <Route path="client/:clientId/meeting-note" element={<MeetingNoteFlowPage />} />
          <Route path="client/:clientId/data-export" element={<DataExportFlowPage />} />
        </Route>

        <Route path="client/:clientId">
          <Route index element={<ClientRootRedirect />} />
          <Route path=":tab/:sectionSlug/folder/:folderId" element={<ClientData />} />
          <Route path=":tab/:sectionSlug/:selectedEventId" element={<ClientData />} />
          <Route path=":tab/:sectionSlug/:selectedEventId/:eventTab" element={<ClientData />} />
          <Route path=":tab/:sectionSlug?" element={<ClientData />} />
          <Route element={<RoutePane bg="white" sidebarCollapsed />}>
            <Route path="detected-entities" element={<DetectedEntitiesPage />} />
            <Route path="financial-items/create/:product" element={<CreateFinancialItemPage />} />
            <Route
              path="financial-items/:product/:id/"
              element={<ViewAndEditFinancialItemPage />}
            />
          </Route>
        </Route>

        <Route element={<RoutePane sidebarCollapsed />}>
          <Route path="user/:tab/:id?" element={<UserDetailsPage />} />
        </Route>

        <Route path="company/:tab?/" element={<CompanyPage />} />
        <Route path="company/:tab?/:fundId?" element={<FundDetailPage />} />

        <Route path="admin" element={<AdminRoute />}>
          <Route path=":tab/:id?" element={<AdminPage />} />
        </Route>

        <Route path="/complete-connection-flow" element={<CompleteConnectionFlowPage />} />

        <Route element={<RoutePane sidebarCollapsed />}>
          <Route
            path="/oauth-callback/google-calendar"
            element={<OAuthRedirectPage platform="GOOGLE" />}
          />
          <Route
            path="/oauth-callback/microsoft-calendar"
            element={<OAuthRedirectPage platform="MICROSOFT" />}
          />
          <Route
            path="/oauth-callback/microsoft-docs"
            element={<DocsOAuthRedirectPage platform="MICROSOFT" />}
          />
        </Route>

        <Route element={<RoutePane bg="white" p={0} sidebarCollapsed />}>
          <Route path="/meetings" element={<MeetingsPage />} />
        </Route>
        <Route element={<RoutePane bg="white" p="xxl" sidebarCollapsed />}>
          <Route path="/meetings/:eventId" element={<MeetingDetailPage />} />
          <Route path="/meetings/:eventId/:eventTab" element={<MeetingDetailPage />} />
        </Route>

        <Route path="/view-entity" element={<ViewEntityPage />} />

        {isAfternoon && <Route path="/debug" element={<DebugPage />} />}
        {isAfternoon && (
          <Route element={<RoutePane sidebarCollapsed />}>
            <Route path="secret-menu/:tab?/" element={<SecretMenuPage />} />
          </Route>
        )}
      </Route>
    </Routes>
  );
}

function DeprecatedProtectedRoutes() {
  const { isAfternoon } = useIsAfternoon();
  const { isLoading } = useClients();
  const {
    flagEnabledDocumentEdit,
    flagEnabledMeetingNoteEdit,
    flagEnabledNewSuitabilityReportAccess,
  } = useFlags();

  if (isLoading) return <PageLoader />;

  return (
    <Routes>
      <Route path="/" element={<Outlet />}>
        {/* Client-related routes */}
        <Route index element={<Navigate to="/clients/all" replace />} />
        <Route element={<DeprecatedAppContainer />}>
          <Route path="clients/:tab/:id?" element={<ClientsPage />} />
        </Route>
        <Route path="client/undefined" element={<RedirectUnknownClient />} />

        {flagEnabledDocumentEdit && (
          <>
            <Route
              path="client/:clientId/document/suitability-reports/:id/edit"
              element={<AppContainer bg="white" />}
            >
              <Route index element={<SuitabilityReportEditPage />} />
            </Route>
            <Route
              path="client/:clientId/document/data-exports/:id/edit"
              element={<AppContainer bg="white" />}
            >
              <Route index element={<DataExportEditPage />} />
            </Route>
            <Route
              path="client/:clientId/document/meeting-agendas/:id/edit"
              element={<AppContainer bg="white" />}
            >
              <Route index element={<MeetingAgendaEditPage />} />
            </Route>
          </>
        )}

        {flagEnabledMeetingNoteEdit && (
          <Route
            path="client/:clientId/document/meeting-notes/:id/edit"
            element={<AppContainer bg="white" />}
          >
            <Route index element={<MeetingNoteEditPage />} />
          </Route>
        )}

        {flagEnabledNewSuitabilityReportAccess && (
          <Route
            path="client/:clientId/suitability-report"
            element={<AppContainer skipDefaultSidebar bg="white" />}
          >
            <Route index element={<NewSuitabilityFlow />} />
          </Route>
        )}

        <Route path="client/:clientId">
          <Route element={<DeprecatedAppContainer />}>
            <Route index element={<ClientRootRedirect />} />
            <Route path=":tab/:sectionSlug/folder/:folderId" element={<ClientData />} />
            <Route path=":tab/:sectionSlug/:selectedEventId" element={<ClientData />} />
            <Route path=":tab/:sectionSlug/:selectedEventId/:eventTab" element={<ClientData />} />
            <Route path=":tab/:sectionSlug?" element={<ClientData />} />
          </Route>
          <Route element={<DeprecatedAppContainer bg="white" showChat={false} />}>
            <Route path="detected-entities" element={<DetectedEntitiesPage />} />
            <Route path="financial-items/create/:product" element={<CreateFinancialItemPage />} />
            <Route
              path="financial-items/:product/:id/"
              element={<ViewAndEditFinancialItemPage />}
            />
          </Route>
        </Route>

        <Route element={<DeprecatedAppContainer showChat={false} />}>
          <Route path="user/:tab/:id?" element={<UserDetailsPage />} />
        </Route>

        {/* Company-related routes */}
        <Route element={<DeprecatedAppContainer />}>
          <Route path="company/:tab?/" element={<CompanyPage />} />
          <Route path="company/:tab?/:fundId?" element={<FundDetailPage />} />
        </Route>

        {/* Admin-related routes */}
        <Route path="admin" element={<AdminRoute />}>
          <Route
            path=":tab/:id?"
            element={
              <DeprecatedAppContainer>
                <AdminPage />
              </DeprecatedAppContainer>
            }
          />
        </Route>
      </Route>
      {isAfternoon && <Route path="/debug" element={<DebugPage />} />}
      <Route path="/complete-connection-flow" element={<CompleteConnectionFlowPage />} />
      {isAfternoon && (
        <Route element={<DeprecatedAppContainer showChat={false} />}>
          <Route path="secret-menu/:tab?/" element={<SecretMenuPage />} />
        </Route>
      )}
      <Route
        path="/view-entity"
        element={
          <DeprecatedAppContainer>
            <ViewEntityPage />
          </DeprecatedAppContainer>
        }
      />
      <Route
        path="/oauth-callback/google-calendar"
        element={
          <DeprecatedAppContainer showChat={false}>
            <OAuthRedirectPage platform="GOOGLE" />
          </DeprecatedAppContainer>
        }
      />
      <Route
        path="/oauth-callback/microsoft-calendar"
        element={
          <DeprecatedAppContainer showChat={false}>
            <OAuthRedirectPage platform="MICROSOFT" />
          </DeprecatedAppContainer>
        }
      />
      <Route
        path="/oauth-callback/microsoft-docs"
        element={
          <DeprecatedAppContainer showChat={false}>
            <DocsOAuthRedirectPage platform="MICROSOFT" />
          </DeprecatedAppContainer>
        }
      />
      <Route element={<DeprecatedAppContainer showChat={false} p={0} bg="white" />}>
        <Route path="/meetings" element={<MeetingsPage />} />
      </Route>
      <Route element={<DeprecatedAppContainer showChat={false} p="xxl" bg="white" />}>
        <Route path="/meetings/:eventId" element={<MeetingDetailPage />} />
        <Route path="/meetings/:eventId/:eventTab" element={<MeetingDetailPage />} />
      </Route>
    </Routes>
  );
}

function ClientRootRedirect() {
  const { clientId } = useCurrentClientIfAvailable();
  const clientDataRootPath = `/client/${clientId}/${ClientTabs.NonFinancialData}`;

  return <Navigate to={clientDataRootPath} replace />;
}
